import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NEW_OFFERS_API, X_APP_ID_HEADER_VALUE } from '@rci/self-serve-fido/app.constant';
import { ErrorModalData, ErrorModalService } from '@rci/self-serve-fido/shared/error-modal';
import { modalWindowConfig } from '@rci/self-serve-fido/shared/modal-window.config';
import { ViewContainerRefService } from '@rci/self-serve-fido/shared/view-container-ref/view-container-ref.service';
import { ApiEndpointConfigService, ApiEndpointService } from '@rci/self-serve-shared';
import { ModalInjectableData } from '@rogers/cdk/common/classes';
import { ModalService } from '@rogers/cdk/modal';
import {
  BpoOfferSuccessModalComponent,
  OfferDetail,
  TargetedOffersFacadeService,
} from '@rogers/self-serve-targeted-offers';
import { of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { AlertFacadeService } from '@rogers/self-serve-common/alert';
import { UserPermissionFacadeService } from '@rogers/self-serve-common/user-permission';
import { AccountDetailFacadeService } from '@rogers/self-serve-common/account';
import { AuthenticationService } from '@rci/self-serve-fido/shared/authentication/authentication.service';
import { AnalyticsServiceProxy } from '@rci/self-serve-fido/core/analytics';
import { ICompleteUniversalDataLayer } from '@rogers/analytics';

@Injectable()
export class RedeemOffersService {
  constructor(
    private readonly accountDetailFacadeService: AccountDetailFacadeService,
    private readonly alertFacadeService: AlertFacadeService,
    private readonly analyticsService: AnalyticsServiceProxy,
    private readonly apiEndpointConfigService: ApiEndpointConfigService,
    private readonly apiEndpointService: ApiEndpointService,
    private readonly authenticationService: AuthenticationService,
    private readonly errorModalService: ErrorModalService,
    private readonly http: HttpClient,
    private readonly modalService: ModalService,
    private readonly targetedOffersFacadeService: TargetedOffersFacadeService,
    private readonly translate: TranslateService,
    private readonly userPermissionFacadeService: UserPermissionFacadeService,
    private readonly viewContainerRefService: ViewContainerRefService,
  ) {}

  redeemOffer$(targetedOffer: OfferDetail) {
    return this.userPermissionFacadeService.hasUserPermission$(NEW_OFFERS_API).pipe(
      switchMap(hasPermission => {
        return hasPermission
          ? this.redeemOfferUsingNewApi$(targetedOffer)
          : this.validateAndSubmitBPOOffer(targetedOffer);
      }),
      map(() => {
        this.updateStoreAfterOfferRedemption(targetedOffer);
        const bpoName =
          this.translate.currentLang === 'en'
            ? targetedOffer.noTransactionalBpo.displayName.en
            : targetedOffer.noTransactionalBpo.displayName.fr;
        const isDataBonusBpo = targetedOffer.noTransactionalBpo.bpoType === 'DATA_BONUS_BPO';
        this.showBPOOfferSuccessModal(bpoName, isDataBonusBpo, targetedOffer.noTransactionalBpo.bpoId);
        return true;
      }),
      catchError(_ => {
        this.showErrorModal();
        return of(false);
      }),
    );
  }

  private redeemOfferUsingNewApi$(targetedOffer: OfferDetail) {
    return this.accountDetailFacadeService.getAccountDetail$(targetedOffer.accountNumber).pipe(
      take(1),
      switchMap(accountDetail => {
        if (!accountDetail || accountDetail.hasError || !accountDetail.subscriptions) {
          throw new Error('Account detail not found');
        }
        const subscription = accountDetail.subscriptions.find(sub => sub.subNumber === targetedOffer.ctn);
        if (!subscription) {
          throw new Error('Subscription not found');
        }
        const url = `${this.apiEndpointService.promotionsBaseUrl}accounts/${accountDetail.alias}/subscription/${subscription.alias}/offer`;
        return this.http.post(
          url,
          { socCode: targetedOffer.noTransactionalBpo.bpoId, skuId: targetedOffer.noTransactionalBpo.skuId },
          { headers: this.authenticationService.getDomainServiceHeaders() },
        );
      }),
    );
  }

  private validateAndSubmitBPOOffer(targetedOffer: OfferDetail) {
    const url = `${this.apiEndpointConfigService.buyflowApiBaseUrl}v1/checkout/validateBPOOffer`;
    const request = {
      accountNumber: targetedOffer.accountNumber,
      phoneNumber: targetedOffer.ctn,
      externalId: targetedOffer.noTransactionalBpo.bpoId,
      skuId: targetedOffer.noTransactionalBpo.skuId,
      applicationId: X_APP_ID_HEADER_VALUE,
      language: this.translate.currentLang,
      sessionKey: '',
    };

    return this.http.post(url, request).pipe(
      switchMap((response: any) => {
        request.sessionKey = response.sessionKey ? response.sessionKey : '';
        return this.submitBPOOffer(request);
      }),
    );
  }

  private submitBPOOffer(request: {
    accountNumber: string;
    phoneNumber: string;
    externalId: string;
    skuId: string;
    applicationId: string;
    language: string;
    sessionKey: string;
  }) {
    const url = `${this.apiEndpointConfigService.buyflowApiBaseUrl}v1/checkout/submitBPOOffer`;
    return this.http.post(url, request);
  }

  private updateStoreAfterOfferRedemption(targetedOffer: OfferDetail) {
    this.targetedOffersFacadeService.clearStandAloneOffer(targetedOffer.accountNumber, targetedOffer.ctn);
    if (!targetedOffer.tvmPresent) {
      this.alertFacadeService.dismissAlert(`${targetedOffer.ctn}_targeted_offer`);
    }
  }

  private showBPOOfferSuccessModal(bpoName: string, isDataBonusBpo: boolean, bpoId: string) {
    const successModalData = new ModalInjectableData({
      heading: 'buyflow.bpo-offers-validation-modal.success-title',
      content: isDataBonusBpo
        ? 'buyflow.bpo-offers-validation-modal.success-data-bonus-content'
        : 'buyflow.bpo-offers-validation-modal.success-content',
      bpoName,
    });
    this.modalService.open(
      BpoOfferSuccessModalComponent,
      this.viewContainerRefService.viewContainerRef,
      modalWindowConfig,
      successModalData,
    );
    const payload: ICompleteUniversalDataLayer = {
      page: {
        name: 'modal-bpo-offer-success',
        hierarchy: ['myaccount', 'overview'],
      },
      events: {
        buyFlowComplete: true,
      },
      buyflow: {
        useCase: `${bpoId}|bpo`,
        name: 'bpo',
      },
    };
    this.analyticsService.track(payload, true, null, null);
  }

  private showErrorModal() {
    const headerText = 'buyflow.bpo-offers-validation-modal.error-title';
    const bodyText = 'buyflow.bpo-offers-validation-modal.error-content';

    const errorModalData: ErrorModalData = {
      headerText,
      bodyText,
      variant: 'error',
    };

    this.errorModalService.showErrorModal(errorModalData);
  }
}
